import Stripe from "stripe";
import {
  auth,
  googleProvider,
  firestore,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  doc,
  setDoc,
  getDoc,
  analytics,
  logEvent,
  messaging,
  getToken,
  onMessage,
  fServerTimestamp,
  sendPasswordResetEmail,
  updateDoc,
} from "./firebaseConfig";

import { generateRandomUsername } from "../utils/ranUsername";
import { generateReferralCode } from "../utils/referralCode";
import { initializeFeedback } from "../services/feedbackService";
import { sendFreeTradeNotification } from "../services/notificationService";

import { toast, ToastContainer } from "react-toastify";
import Message from "../components/notification/Message";
import "react-toastify/dist/ReactToastify.css";

// Initialize Stripe safely
let stripe;
try {
  const stripeSecretKey = import.meta.env.VITE_API_STRIPE_SECRET_KEY;
  if (!stripeSecretKey) throw new Error("Missing Stripe secret key");
  stripe = new Stripe(stripeSecretKey);
} catch (error) {
  console.error("Error initializing Stripe:", error.message);
}

/*     let avatarURL = "";
  
    // If avatarFile is provided, upload it to Firebase Storage
    if (avatarFile) {
      const storageRef = sRef(storage, `avatars/${newUser.uid}_${Date.now()}`);
      const uploadTask = await uploadBytesResumable(storageRef, avatarFile);
      avatarURL = await getDownloadURL(uploadTask.ref);
    } */
  

/**
 * Create a new user with email and password.
 */
export const createUser = async (email, password, additionalInfo = {}) => {
  console.log("Starting user creation process...");

  if (!email || !password) {
    throw new Error("Email and password are required.");
  }

  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const newUser = userCredential.user;

    if (!newUser?.uid) {
      throw new Error("User creation failed.");
    }

    console.log("Firebase Auth User Created:", newUser.uid);
    
    // **Ensure username and referral code are fully resolved before storing in Firestore**
    const randomUsername = await generateRandomUsername();
    const referralCode = await generateReferralCode(newUser.uid);

    console.log(`Generated Referral Code: ${referralCode}`); // Debugging


    const userDocRef = doc(firestore, "users", newUser.uid);

    // Create Stripe Customer
    const stripeCustomer = await stripe?.customers.create({ email: newUser.email });

    let referredByCode = additionalInfo.referredByCode || null;

    // Validate referral
/*     if (referredByCode) {
      const referrerRef = doc(firestore, "users", referredByCode);
      const referrerSnap = await getDoc(referrerRef);

      if (referrerSnap.exists()) {
        freeTrades += 1; // Referred users get 1 extra free trade
        console.log(`User was referred by ${referredByCode}, granting extra free trade.`);
        await updateDoc(referrerRef, { freeTrades: (referrerSnap.data().freeTrades || 0) + 1 });

         // **Send referral notification to referred user**
         await sendFreeTradeNotification(newUser.uid, freeTrades);
      } else {
        console.warn(`Invalid referral code: ${referredByCode}. Proceeding without referral bonus.`);
        referredByCode = null; // Reset referral code if invalid
      }
    }

    console.log(`My referral code: ${referralCode} and referred by: ${referredByCode}`); */


    // Save user data in Firestore
    const userData = {
      email: newUser.email,
      userName: randomUsername,
      stripeCustomerId: stripeCustomer?.id || "",
      accountType: "personal",
      quotaUsed: 0,
      trustScore: 0,
      emailVerified: false,
      phoneVerified: false,
      isProfileUpdated: false,
      kycVerified: false,
      freeTrades: 0, // No free trades until verification
      referralCode,
      referredByCode, // Store referral but delay awarding free trades
      createdAt: fServerTimestamp(),
      ...additionalInfo,
    };
    

    console.log("User data to be saved:", userData); // Debugging


    await setDoc(userDocRef, userData);
    console.log("User data saved to Firestore:", userData);

    await initializeFeedback(newUser.uid);

    if (analytics) logEvent(analytics, "user_signed_up", { email, username: randomUsername });

    return newUser;
  } catch (error) {
    console.error("Error during user creation:", error);
    throw new Error(error.message || "User creation failed.");
  }
};



/**
 * Log in a user with email and password.
 */
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    if (analytics) logEvent(analytics, "user_logged_in", { email });

    console.log("User logged in:", user.uid);
    return user;
  } catch (error) {
    console.error("Login error:", error);
    throw new Error(error.message || "Login failed.");
  }
};

/**
 * Log in with Google and apply referral code if available.
 */
export const loginWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const googleUser = result.user;

    if (!googleUser?.uid) {
      throw new Error("Google authentication failed. No valid user ID.");
    }

    const userDocRef = doc(firestore, "users", googleUser.uid);
    const userSnapshot = await getDoc(userDocRef);

    // **Check if the user already exists**
    if (userSnapshot.exists()) {
      console.log("Existing Google user detected:", googleUser.uid);
      return googleUser;
    }

    // **Retrieve referral code from sessionStorage (if available)**
    let referredByCode = sessionStorage.getItem("referredByCode") || null;

    // **Ensure username and referral code are generated before storing**
    const randomUsername = await generateRandomUsername();
    const referralCode = await generateReferralCode(googleUser.uid);

    // **Create Stripe customer if necessary**
    let stripeCustomer;
    try {
      stripeCustomer = await stripe?.customers.create({ email: googleUser.email });
    } catch (error) {
      console.warn("Stripe customer creation failed:", error.message);
    }

/*     // **Prepare user data for Firestore**
    let freeTrades = 2; // Default free trades

    // **If the user was referred, reward the referrer**
    if (referredByCode) {
      const referrerRef = doc(firestore, "users", referredByCode);
      const referrerSnap = await getDoc(referrerRef);

      if (referrerSnap.exists()) {
        await updateDoc(referrerRef, { freeTrades: (referrerSnap.data().freeTrades || 0) + 1 });
        await updateDoc(userDocRef, { freeTrades: 3 }); // Referred users get 1 extra free trade (total: 3)
        console.log(`User was referred by ${referredByCode}, rewarding referrer and referred user.`);

        // **Send free trade notification**
        await sendFreeTradeNotification(googleUser.uid, freeTrades);
      } else {
        referredByCode = null; // Reset referral if invalid

        console.warn(`Invalid referral code: ${referredByCode}.`);
      }
    } */

    // **Prepare user data for Firestore**
    const userData = {
      email: googleUser.email,
      userName: randomUsername,
      avatar: googleUser.photoURL || "",
      accountType: "personal",
      stripeCustomerId: stripeCustomer?.id || "",
      quotaUsed: 0,
      trustScore: 0,
      emailVerified: true, // Google login automatically verifies email
      phoneVerified: false,
      isProfileUpdated: false,
      kycVerified: false,
      freeTrades: 0, // ✅ New users get 2 free trades
      referralCode,
      referredByCode,
      createdAt: fServerTimestamp(),
    };

    // **Save the new Google user to Firestore**
    await setDoc(userDocRef, userData);
    console.log("New Google user data saved to Firestore:", googleUser.uid);

    await initializeFeedback(googleUser.uid);

    if (analytics) logEvent(analytics, "google_sign_in", { email: googleUser.email });

    return googleUser;
  } catch (error) {
    console.error("Google sign-in error:", error);
    throw new Error("Google sign-in failed.");
  }
};

/**
 * Send a password reset email.
 */
export const sendPasswordReset = async (email) => {
  if (!email) throw new Error("Email is required.");
  await sendPasswordResetEmail(auth, email);
};


/**
 * Register FCM token and subscribe to notifications.
 */
export const registerFCMToken = async (userId) => {
  try {
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications.");
      return;
    }

    console.log("Requesting notification permission...");
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      console.warn("Notification permission denied.");
      return;
    }

    const token = await getToken(messaging, { vapidKey: import.meta.env.VITE_API_APP_VAPID_KEY });

    //We can send token to server
    console.log("Token generated : ", token);

    await setDoc(doc(firestore, "userTokens", userId), {
      token,
      createdAt: fServerTimestamp(),
      updatedAt: fServerTimestamp(),
    });

    console.log("FCM token saved in Firestore.");
  } catch (error) {
    console.error("Error registering FCM token:", error.message);
  }
};

/**
 * Log out the current user.
 */
export const logOut = async () => {
  try {
    await signOut(auth);
    if (analytics) logEvent(analytics, "user_logged_out");
    console.log("User logged out.");
  } catch (error) {
    console.error("Logout error:", error);
    throw new Error("Logout failed.");
  }
};

/**
 * Listen to authentication state changes.
 */
export const onAuthStateChangedListener = (callback) => {
  return onAuthStateChanged(auth, callback);
};

